import classNames from 'classnames';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import styles from './two-column-text-block.module.scss';

export interface TwoColumnTextBlockProps {
  /** The class name to apply */
  className?: string;
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The text to show as a headline */
  headline?: string | null;
  /** The text to show as a pre headline */
  preHeadline?: string | null;
  /** The text to show as a sub headline */
  subHeadline?: string | null;
  /** The rich text to show in the left column */
  text1?: string | null;
  /** The rich text to show in the right column */
  text2?: string | null;
}

const TwoColumnTextBlock = ({
  className,
  ctaLinks = [],
  headline,
  preHeadline,
  subHeadline,
  text1,
  text2,
}: TwoColumnTextBlockProps) => {
  return (
    <div className={classNames(styles.component, className)}>
      <Container className={styles.container}>
        {(headline || preHeadline || subHeadline) && (
          <Row>
            <Column
              className={styles.contentColumn}
              offset={{ laptop: 2 }}
              width={{ laptop: 8 }}
            >
              {(headline || preHeadline) && (
                <div className={styles.headlineWrapper}>
                  {preHeadline && (
                    <Typography
                      className={styles.preHeadline}
                      tag="small"
                      tagStyle="labelMedium"
                    >
                      {preHeadline}
                    </Typography>
                  )}

                  {headline && (
                    <Typography
                      className={styles.headline}
                      tag="h2"
                      tagStyle="displayMedium"
                    >
                      {headline}
                    </Typography>
                  )}
                </div>
              )}

              {subHeadline && (
                <Typography
                  className={styles.subHeadline}
                  tag="h3"
                  tagStyle="bodyLargeBold"
                >
                  {subHeadline}
                </Typography>
              )}
            </Column>
          </Row>
        )}

        <Row className={styles.textRow}>
          <Column offset={{ laptop: 2 }} width={{ laptop: 4 }}>
            {text1 && <RichText text={text1} />}
          </Column>

          <Column width={{ laptop: 4 }}>
            {text2 && <RichText text={text2} />}
          </Column>
        </Row>

        {ctaLinks.length > 0 && (
          <Row className={styles.linksRow}>
            <Column offset={{ laptop: 2 }} width={{ laptop: 8 }}>
              <CtaLinkList items={ctaLinks} />
            </Column>
          </Row>
        )}
      </Container>
    </div>
  );
};

TwoColumnTextBlock.displayName = 'TwoColumnTextBlock';

export default TwoColumnTextBlock;
